import { useEffect, useMemo, useState } from 'react'
import {
    ScoopButton,
    ScoopText,
    ScoopSwitch,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
} from '../../../../ui'
import { Server } from '../../../../../api/Server'
import { useSelector } from 'react-redux'
import { ServerContext } from '../../SheetState'
import { renderToString } from 'react-dom/server'
import googleSheetsIcon from '../../../../../assets/source/GoogleSheets.svg'
import Sheetlet from '../../Sheetlet/Sheetlet'
import BlendingModal from '../../NewDatasetModal/Blending/BlendingModal'
import { useWorkspaceMetadata } from '../../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import styles from './EditCalculatedSource.module.scss'

const EditCalculatedSource = ({ inboxID, singleQuery, setSingleQuery }) => {
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const token = useSelector((state) => state.auth.token)

    const { workspaceMetadata: metadata } = useWorkspaceMetadata(userID, workspaceID, token)

    const [queries, setQueries] = useState([])
    const [worksheetID, setWorksheetID] = useState(null)
    const [isEditingQuery, setIsEditingQuery] = useState(false)
    const [save, setSave] = useState(false)
    const [errorQueries, setErrorQueries] = useState(null)
    const [errorSheetlet, setErrorSheetlet] = useState(false)
    const [indexEditingQuery, setIndexEditingQuery] = useState(null)
    const [isAdvanced, setIsAdvanced] = useState(false)
    const serverContext = new ServerContext(new Server(workspaceID, userID, token))
    const [triggerGetSheetCall, setTriggerGetSheetCall] = useState(false)
    const [sheetRange, setSheetRange] = useState({
        sheetType: 'CalculatedSource',
        inboxID: inboxID,
        advanced: isAdvanced,
    })

    useEffect(() => {
        setSheetRange({
            sheetType: 'CalculatedSource',
            inboxID: inboxID,
            advanced: isAdvanced,
        })
    }, [isAdvanced])

    useEffect(() => {
        getOutputDefinition()
    }, [save])

    const getOutputDefinition = async () => {
        try {
            await serverContext.server.sheetPostData(
                {
                    action: 'getCSWDefinition',
                    inboxID: inboxID,
                },
                processOutputDefinition
            )
        } catch (e) {
            console.error(e)
        }
    }

    const processOutputDefinition = (results) => {
        setErrorSheetlet(false)
        setTriggerGetSheetCall((prev) => !prev)
        if (results.queries.length === 0) setErrorQueries('No data available')
        setSingleQuery(results.queries.length === 1)
        setQueries(results.queries)
        setWorksheetID(results.worksheetID)
    }

    const handleSaveSheetlet = async () => {
        try {
            await serverContext.server.sheetPostData({
                action: 'flushWorksheetCache',
                sheetRange: {
                    inboxID: inboxID,
                },
            })
        } catch (e) {
            console.error(e)
        }
    }

    const renderSheetlet = useMemo(() => {
        return (
            <Sheetlet
                key={triggerGetSheetCall}
                isBlending={true}
                serverContext={serverContext}
                sheetRange={sheetRange}
                locked={!isAdvanced}
                embeddedSizeProps={{ height: 'auto' }}
                controlledHeight={true}
                cornerHTML={
                    '<a style="position:relative; z-index: 0 !important;" href="https://docs.google.com/spreadsheets/d/' +
                    worksheetID +
                    '" target="_blank">' +
                    renderToString(<img alt={'Google sheets'} src={googleSheetsIcon} />) +
                    '</a>'
                }
                addNew={getOutputDefinition}
                setError={setErrorSheetlet}
            />
        )
    }, [worksheetID, save, sheetRange, queries])

    return (
        <ScoopRow className={styles.mainContainer}>
            <ScoopColumn className={styles.contentWrapper}>
                <ScoopColumn className={styles.innerContent}>
                    {!errorQueries && (
                        <ScoopRow
                            className={`${styles.queriesContainer} ${
                                singleQuery ? styles.singleQueryWidth : styles.fullWidth
                            }`}
                        >
                            {queries.length > 0 && (
                                <>
                                    <Query
                                        handleEdit={() => {
                                            setIndexEditingQuery(0)
                                            setIsEditingQuery(true)
                                        }}
                                        query={queries[0]}
                                        index={1}
                                    />
                                    {!singleQuery && (
                                        <Query
                                            handleEdit={() => {
                                                setIndexEditingQuery(1)
                                                setIsEditingQuery(true)
                                            }}
                                            query={queries[1]}
                                            index={2}
                                        />
                                    )}
                                </>
                            )}
                        </ScoopRow>
                    )}
                    {errorQueries && (
                        <ScoopCenter className={styles.errorContainer}>
                            <ScoopText className={styles.errorText}>{errorQueries}</ScoopText>
                        </ScoopCenter>
                    )}
                    <ScoopColumn>
                        {!errorSheetlet && !errorQueries ? (
                            <ScoopColumn className={styles.sheetletSection}>
                                <ScoopRow className={styles.sheetletHeader}>
                                    <ScoopRow className={styles.advancedToggle}>
                                        <ScoopText className={styles.advancedLabel}>
                                            Advanced
                                        </ScoopText>
                                        <ScoopSwitch
                                            checked={isAdvanced}
                                            onChange={() => setIsAdvanced(!isAdvanced)}
                                        />
                                    </ScoopRow>
                                    <ScoopButton size="xs" onClick={handleSaveSheetlet}>
                                        Save Blend
                                    </ScoopButton>
                                </ScoopRow>
                                {renderSheetlet}
                            </ScoopColumn>
                        ) : (
                            <ScoopColumn>
                                <ScoopText className={styles.errorText}>{errorSheetlet}</ScoopText>
                            </ScoopColumn>
                        )}
                    </ScoopColumn>
                </ScoopColumn>
            </ScoopColumn>
            {isEditingQuery && metadata && (
                <BlendingModal
                    metadata={metadata}
                    queryName={queries[indexEditingQuery]?.sheetName}
                    index={indexEditingQuery}
                    setStep={() => {}}
                    goForward={() => {
                        setIsEditingQuery(false)
                        setSave(!save)
                    }}
                    isEditing={true}
                    isBlending={!singleQuery}
                    inboxID={inboxID}
                    worksheetID={worksheetID}
                    onClose={() => setIsEditingQuery(false)}
                />
            )}
        </ScoopRow>
    )
}

export default EditCalculatedSource

export const Query = ({ query, handleEdit, index }) => {
    const columns = query?.columns.map((col) => col.columnName).join(', ')

    return (
        <ScoopColumn className={styles.queryCard}>
            <ScoopRow className={styles.queryHeader}>
                <ScoopText className={styles.queryTitle}>
                    {query ? query.sheetName : `Query ${index}`}
                </ScoopText>
                <ScoopButton onClick={handleEdit} size="xs">
                    Edit
                </ScoopButton>
            </ScoopRow>
            <ScoopColumn className={styles.queryContent}>
                <ScoopText className={styles.queryDetailText}>
                    <span className={styles.queryLabel}>Dataset: </span>
                    {query ? query.inbox : 'N/A'}
                </ScoopText>
                <ScoopText className={styles.queryDetailText}>
                    <span className={styles.queryLabel}>Columns: </span>
                    {columns ? columns : 'N/A'}
                </ScoopText>
            </ScoopColumn>
        </ScoopColumn>
    )
}
